import React, { useEffect, useCallback } from 'react';
import { createRoot, Root } from 'react-dom/client';
import './Toast.scss';

export type ToastType = 'success' | 'warning' | 'error' | 'info';

interface ToastProps {
    id: string;
    type: ToastType;
    title?: string;
    message: string;
    duration?: number;
    onClose: (id: string) => void;
}

const ToastItem: React.FC<ToastProps> = ({ id, type, title, message, duration = 5000, onClose }) => {
    useEffect(() => {
        if (duration > 0) {
            const timer = setTimeout(() => onClose(id), duration);
            return () => clearTimeout(timer);
        }
    }, [id, duration, onClose]);

    const icons: Record<ToastType, string> = {
        success: '✓',
        warning: '⚠',
        error: '✕',
        info: 'ℹ',
    };

    return (
        <div className={`toast toast-${type}`}>
            <span className="toast-icon">{icons[type]}</span>
            <div className="toast-content">
                {title && <div className="toast-title">{title}</div>}
                <div className="toast-message">{message}</div>
            </div>
            <button className="toast-close" onClick={() => onClose(id)}>
                ×
            </button>
        </div>
    );
};

// Toast container component
interface ToastContainerProps {
    toasts: ToastProps[];
    onClose: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onClose }) => {
    if (toasts.length === 0) return null;

    return (
        <div className="toast-container">
            {toasts.map((toast) => (
                <ToastItem key={toast.id} {...toast} onClose={onClose} />
            ))}
        </div>
    );
};

// Global toast state
let toastRoot: Root | null = null;
let toasts: ToastProps[] = [];
const listeners: Set<(toasts: ToastProps[]) => void> = new Set();

const notifyListeners = () => {
    listeners.forEach((listener) => listener([...toasts]));
};

// Create toast container in DOM
const createToastContainer = () => {
    if (toastRoot) return;
    
    const container = document.createElement('div');
    container.className = 'toast-wrapper';
    document.body.appendChild(container);
    toastRoot = createRoot(container);
    
    const render = () => {
        toastRoot!.render(<ToastContainer toasts={toasts} onClose={removeToast} />);
    };
    
    listeners.add(render);
    render();
};

const removeToast = (id: string) => {
    toasts = toasts.filter((t) => t.id !== id);
    notifyListeners();
};

interface ShowToastOptions {
    type?: ToastType;
    title?: string;
    duration?: number;
}

export const toast = {
    success: (message: string, options?: Omit<ShowToastOptions, 'type'>) => {
        showToast(message, { ...options, type: 'success' });
    },
    
    error: (message: string, options?: Omit<ShowToastOptions, 'type'>) => {
        showToast(message, { ...options, type: 'error' });
    },
    
    warning: (message: string, options?: Omit<ShowToastOptions, 'type'>) => {
        showToast(message, { ...options, type: 'warning' });
    },
    
    info: (message: string, options?: Omit<ShowToastOptions, 'type'>) => {
        showToast(message, { ...options, type: 'info' });
    },
};

const showToast = (message: string, options: ShowToastOptions = {}) => {
    if (!toastRoot) {
        createToastContainer();
    }
    
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    toasts.push({
        id,
        type: options.type || 'info',
        title: options.title,
        message,
        duration: options.duration,
        onClose: removeToast,
    });
    
    notifyListeners();
};

// Component wrapper for using toast context
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    useEffect(() => {
        createToastContainer();
    }, []);
    
    return <>{children}</>;
};
